import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { createServiceCategory, updateServiceCategory } from "../../apis/serviceCategory";

interface FormData {
  code: string;
  displayName: string;
  source: string;
  isClaimable: boolean;
  isPharmacy: boolean;
  description: string;
  displayOrder: number;
  isActive: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editableId?: number | null;
  categoryData?: FormData | null;
}

const defaultForm: FormData = {
  code: "",
  displayName: "",
  source: "",
  isClaimable: true,
  isPharmacy: false,
  description: "",
  displayOrder: 0,
  isActive: true,
};

const AddServiceCategoryModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  editableId,
  categoryData,
}) => {
  const [form, setForm] = useState<FormData>(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEdit = Boolean(editableId);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit && categoryData ? categoryData : defaultForm);
    setErrors({});
  }, [open, isEdit, categoryData]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.code.trim()) e.code = "* Code is required";
    if (!form.displayName.trim()) e.displayName = "* Display name is required";
    if (!form.source.trim()) e.source = "* Source is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      if (isEdit) {
        await updateServiceCategory(editableId!, form);
      } else {
        await createServiceCategory(form);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving service category:", error);
    }
  };

  if (!open) return null;

  return (
    <Modal
      title={isEdit ? "Edit Service Category" : "Add Service Category"}
      onClose={onClose}
      modalContent={
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Code</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. PROFESSIONAL_FEES"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
            {errors.code && <span className="text-xs text-red-500">{errors.code}</span>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Display Name</label>
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="e.g. Professional Fees"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
            {errors.displayName && (
              <span className="text-xs text-red-500">{errors.displayName}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Source</label>
            <input
              type="text"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="e.g. internal"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
            {errors.source && <span className="text-xs text-red-500">{errors.source}</span>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Display Order</label>
            <input
              type="number"
              value={form.displayOrder}
              min={0}
              onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isClaimable}
                onChange={(e) => setForm({ ...form, isClaimable: e.target.checked })}
              />
              Is Claimable
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPharmacy}
                onChange={(e) => setForm({ ...form, isPharmacy: e.target.checked })}
              />
              Is Pharmacy
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Is Active
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={handleSave}
              className="px-3 py-2 text-sm bg-primary text-white rounded"
            >
              {isEdit ? "Update" : "Save"}
            </button>
            <button onClick={onClose} className="px-3 py-2 text-sm border rounded">
              Cancel
            </button>
          </div>
        </div>
      }
    />
  );
};

export default AddServiceCategoryModal;
